import { DicomRoi } from '../dicom/dicomRoi';
import { RoiInterpretedType } from '../dicom/dicomstructureset';
import { StructureSet } from '../patient/structureset';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '../readmodels/StructureSetReadModel';

export class StructureSetRepository {
    private groundTruthStore = new Map<string, StructureSet>();

    getGroundTruth(uid: string): StructureSet {
        const groundTruth = this.groundTruthStore.get(uid);
        if (!groundTruth) {
            throw new Error(
                `StructureSet with UID ${uid} not found in repository`
            );
        }
        return groundTruth;
    }

    saveGroundTruth(structureSet: StructureSet): StructureSetReadModel {
        this.groundTruthStore.set(structureSet.dicomUid, structureSet);
        return this.createReadModel(structureSet);
    }

    updateRoi(
        structureSetUid: string,
        roiNumber: number,
        handleMutation: (roi: DicomRoi) => void
    ): StructureSetReadModel {
        this.ensureExists(structureSetUid);
        const groundTruth = this.getGroundTruth(structureSetUid);
        const roi = groundTruth.getRoiByNumber(roiNumber);

        if (!roi) {
            throw new Error(
                `ROI ${roiNumber} not found in StructureSet ${structureSetUid}`
            );
        }

        handleMutation(roi);
        return this.createReadModel(groundTruth);
    }

    updateStructureSet(
        structureSetUid: string,
        handleMutation: (structureSet: StructureSet) => void
    ): StructureSetReadModel {
        this.ensureExists(structureSetUid);
        const groundTruth = this.getGroundTruth(structureSetUid);
        handleMutation(groundTruth);
        return this.createReadModel(groundTruth);
    }

    createRoi(
        structureSetUid: string,
        interpretedType?: RoiInterpretedType,
        structureName: string = ''
    ): {
        structureSetReadModel: StructureSetReadModel;
        newRoi: DicomRoi;
    } {
        this.ensureExists(structureSetUid);
        const groundTruth = this.getGroundTruth(structureSetUid);

        const newRoi = groundTruth.dicomStructureSet.createRoi(
            interpretedType,
            structureName
        );

        return {
            structureSetReadModel: this.createReadModel(groundTruth),
            newRoi,
        };
    }

    deleteRoi(
        structureSetUid: string,
        roiNumber: number
    ): StructureSetReadModel {
        this.ensureExists(structureSetUid);
        const groundTruth = this.getGroundTruth(structureSetUid);

        if (!groundTruth.getRoiByNumber(roiNumber)) {
            throw new Error(
                `ROI ${roiNumber} not found in StructureSet ${structureSetUid}`
            );
        }

        groundTruth.dicomStructureSet.deleteRoi(roiNumber);
        return this.createReadModel(groundTruth);
    }

    exists(uid: string): boolean {
        return this.groundTruthStore.has(uid);
    }

    private ensureExists(uid: string): void {
        if (!this.exists(uid)) {
            throw new Error(
                `StructureSet with UID ${uid} not found in repository. Make sure to call saveGroundTruth() first.`
            );
        }
    }

    getReadModel(uid: string): StructureSetReadModel {
        const groundTruth = this.getGroundTruth(uid);
        return this.createReadModel(groundTruth);
    }

    delete(uid: string): void {
        this.groundTruthStore.delete(uid);
    }

    /** Removes all ground truth entries from the repository. */
    clear(): void {
        this.groundTruthStore.clear();
    }

    getAllUids(): string[] {
        return Array.from(this.groundTruthStore.keys());
    }

    private createReadModel(groundTruth: StructureSet): StructureSetReadModel {
        const roiReadModels: RoiReadModel[] = groundTruth.roiArray.map((roi) =>
            Object.freeze({
                roiNumber: roi.roiNumber,
                name: roi.name,
                roiDescription: roi.roiDescription,
                color: Object.freeze({ ...roi.color }),
                visible: roi.visible,
                contourThickness: roi.contourThickness,
                approvalStatus: roi.approvalStatus,
                interpretedType: roi.interpretedType,
                electronDensityOverride: roi.electronDensityOverride,
                massDensityOverride: roi.massDensityOverride,
                roiGenerationAlgorithm: roi.roiGenerationAlgorithm,
                contourCount: roi.contours.length,
                isApproved: roi.isApproved,
                isPointRoi: roi.isPointRoi,
                identificationCode: roi.identificationCode,
                pointCoordinatesMm: roi.pointCoordinatesMm,
            })
        );

        return Object.freeze({
            dicomUid: groundTruth.dicomUid,
            structureSetLabel: groundTruth.structureSetLabel,
            structureSetDescription: groundTruth.structureSetDescription,
            approvalStatus: groundTruth.approvalStatus,
            isApproved: groundTruth.isApproved,
            reviewerName: groundTruth.reviewerName,
            reviewDate: groundTruth.reviewDate,
            reviewTime: groundTruth.reviewTime,
            frameOfReferenceUid: groundTruth.frameOfReferenceUid,
            numberOfStructures: groundTruth.numberOfStructures,
            roiArray: Object.freeze(roiReadModels),
            manufacturer: groundTruth.manufacturer,
            manufacturerModelName: groundTruth.manufacturerModelName,
            softwareVersions: groundTruth.softwareVersions,
            hasImage: groundTruth.image !== null,
            imageUid: groundTruth.image?.dicomUid ?? null,
            generationModel: groundTruth.generationModel,
        });
    }
}
